import React from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import moment from 'moment';

import CustomDialog from 'components/Dialog/Dialog';
import { auditDashboardTheme } from 'features/auditDashboard/theme';
import { formatCurrency } from 'utils';
import {
  StoreInvestigationReport,
  StoreInvestigationRootCauseAudit,
} from '../services/StoresService';

export interface StoreInvestigationTarget {
  storeName: string;
  businessDate: string;
  auditQuantity: number;
  posQuantity: number;
  quantityVariance: number;
  auditSales: number;
  posSales: number;
  salesVariance: number;
}

interface StoreInvestigationDialogProps {
  open: boolean;
  target: StoreInvestigationTarget | null;
  report: StoreInvestigationReport | null;
  isLoading: boolean;
  error: string | null;
  onClose: () => void;
}

const formatSignedNumber = (value: number): string => value > 0 ? `+${value}` : value.toString();
const formatSignedCurrency = (value: number): string => {
  if (value > 0) return `+${formatCurrency(value)}`;
  return formatCurrency(value);
};

const outcomeLabel: Record<StoreInvestigationReport['outcome'], string> = {
  matched: 'No issue identified',
  mismatched: 'Investigation complete',
  insufficient_data: 'More data required',
  failed: 'Investigation failed',
};

interface RootCauseAuditGroup {
  auditId: string;
  auditAt: string;
  operatorName: string | null;
  gamePacks: Array<{ gameId: string; packId: string }>;
}

export const groupRootCauseAudits = (
  audits: StoreInvestigationRootCauseAudit[] = [],
): RootCauseAuditGroup[] => {
  const groups = new Map<string, RootCauseAuditGroup>();

  audits.forEach((audit) => {
    const key = `${audit.audit_id}-${audit.audit_at}`;
    const group = groups.get(key) ?? {
      auditId: audit.audit_id,
      auditAt: audit.audit_at,
      operatorName: audit.operator_name?.trim() || null,
      gamePacks: [],
    };
    group.gamePacks.push({ gameId: audit.game_id, packId: audit.pack_id });
    groups.set(key, group);
  });

  return Array.from(groups.values()).map((group) => ({
    ...group,
    gamePacks: group.gamePacks.sort((left, right) =>
      `${left.gameId}-${left.packId}`.localeCompare(`${right.gameId}-${right.packId}`),
    ),
  }));
};

export const formatRootCauseAuditAt = (value: string): string => {
  const parsed = moment.parseZone(value, moment.ISO_8601, true);
  return parsed.isValid()
    ? parsed.format('MMMM D, YYYY [at] h:mm A')
    : value;
};

export const RootCauseAuditDetails: React.FC<{
  audits?: StoreInvestigationRootCauseAudit[];
}> = ({ audits = [] }) => {
  const groups = groupRootCauseAudits(audits);
  if (groups.length === 0) return null;

  return (
    <Box
      aria-label="Root cause audit details"
      sx={{
        bgcolor: auditDashboardTheme.colors.blueSoft,
        borderLeft: `3px solid ${auditDashboardTheme.colors.blue}`,
        borderRadius: '6px',
        mt: 1,
        p: 1,
      }}
    >
      <Typography
        sx={{
          color: auditDashboardTheme.colors.text,
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: 0.35,
          textTransform: 'uppercase',
        }}
      >
        Root cause audit
      </Typography>
      <Stack spacing={1} sx={{ mt: 0.65 }}>
        {groups.map((group) => (
          <Box key={`${group.auditId}-${group.auditAt}`}>
            <Typography sx={{ color: auditDashboardTheme.colors.text, fontSize: 12.5, fontWeight: 700 }}>
              {formatRootCauseAuditAt(group.auditAt)}
            </Typography>
            {group.operatorName && (
              <Typography sx={{ color: auditDashboardTheme.colors.textMutedStrong, fontSize: 12, mt: 0.2 }}>
                Performed by {group.operatorName}
              </Typography>
            )}
            <Typography
              component="code"
              sx={{
                color: auditDashboardTheme.colors.textMutedStrong,
                display: 'block',
                fontFamily: 'monospace',
                fontSize: 11,
                overflowWrap: 'anywhere',
                mt: 0.25,
              }}
            >
              Audit ID: {group.auditId}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.6 }}>
              {group.gamePacks.map(({ gameId, packId }) => (
                <Box
                  component="span"
                  key={`${group.auditId}-${gameId}-${packId}`}
                  sx={{
                    bgcolor: 'background.paper',
                    border: `1px solid ${auditDashboardTheme.colors.lineSoft}`,
                    borderRadius: '5px',
                    color: auditDashboardTheme.colors.text,
                    fontSize: 11.5,
                    fontWeight: 600,
                    px: 0.65,
                    py: 0.2,
                  }}
                >
                  Game {gameId} · Pack {packId}
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

const StoreInvestigationDialog: React.FC<StoreInvestigationDialogProps> = ({
  open,
  target,
  report,
  isLoading,
  error,
  onClose,
}) => (
  <CustomDialog
    open={open}
    handleClose={onClose}
    title="Scratcher Investigation"
    width="760px"
    fullWidth
    className="store-investigation-dialog"
  >
    {target && (
      <Stack spacing={2} sx={{ py: 0.5 }}>
        <Box>
          <Typography sx={{ color: auditDashboardTheme.colors.text, fontSize: 16, fontWeight: 700 }}>
            {target.storeName}
          </Typography>
          <Typography sx={{ color: auditDashboardTheme.colors.textMutedStrong, fontSize: 12.5 }}>
            {moment(target.businessDate).format('MMMM D, YYYY')}
          </Typography>
        </Box>

        <Box
          sx={{
            borderBottom: `1px solid ${auditDashboardTheme.colors.line}`,
            borderTop: `1px solid ${auditDashboardTheme.colors.line}`,
            display: 'grid',
            gridTemplateColumns: 'minmax(120px, 1fr) repeat(3, minmax(100px, 1fr))',
            py: 1,
            rowGap: 0.75,
          }}
        >
          <Typography />
          {['Audit Expected', 'POS Reported', 'Variance'].map((label) => (
            <Typography
              key={label}
              sx={{ color: auditDashboardTheme.colors.text, fontSize: 11, fontWeight: 700, textAlign: 'right', textTransform: 'uppercase' }}
            >
              {label}
            </Typography>
          ))}
          <Typography sx={{ color: auditDashboardTheme.colors.text, fontSize: 13, fontWeight: 600 }}>Scratcher Qty</Typography>
          <Typography sx={{ textAlign: 'right' }}>{target.auditQuantity.toLocaleString('en-US')}</Typography>
          <Typography sx={{ textAlign: 'right' }}>{target.posQuantity.toLocaleString('en-US')}</Typography>
          <Typography sx={{ color: target.quantityVariance < 0 ? auditDashboardTheme.colors.textNegative : auditDashboardTheme.colors.textPositive, fontWeight: 600, textAlign: 'right' }}>
            {formatSignedNumber(target.quantityVariance)}
          </Typography>
          <Typography sx={{ color: auditDashboardTheme.colors.text, fontSize: 13, fontWeight: 600 }}>Scratcher $</Typography>
          <Typography sx={{ textAlign: 'right' }}>{formatCurrency(target.auditSales)}</Typography>
          <Typography sx={{ textAlign: 'right' }}>{formatCurrency(target.posSales)}</Typography>
          <Typography sx={{ color: target.salesVariance < 0 ? auditDashboardTheme.colors.textNegative : auditDashboardTheme.colors.textPositive, fontWeight: 600, textAlign: 'right' }}>
            {formatSignedCurrency(target.salesVariance)}
          </Typography>
        </Box>

        {isLoading && (
          <Box role="status" sx={{ alignItems: 'center', display: 'flex', gap: 1.25, justifyContent: 'center', minHeight: 150 }}>
            <CircularProgress size={22} />
            <Typography sx={{ color: auditDashboardTheme.colors.textMutedStrong, fontSize: 13 }}>
              Investigating the scratcher mismatch…
            </Typography>
          </Box>
        )}

        {error && (
          <Box sx={{ bgcolor: auditDashboardTheme.colors.headerNegative, borderRadius: '8px', p: 1.5 }}>
            <Typography sx={{ color: auditDashboardTheme.colors.textNegative, fontSize: 13, fontWeight: 700 }}>
              {error}
            </Typography>
          </Box>
        )}

        {report && !isLoading && (
          <Stack spacing={1.25}>
            <Typography sx={{ color: auditDashboardTheme.colors.text, fontSize: 15, fontWeight: 800 }}>
              {outcomeLabel[report.outcome]}
            </Typography>

            {report.identified_issues.map((issue) => (
              <Box
                key={`${issue.type}-${issue.impact}`}
                sx={{ bgcolor: auditDashboardTheme.colors.surface3, border: `1px solid ${auditDashboardTheme.colors.lineSoft}`, borderRadius: '8px', p: 1.25 }}
              >
                <Typography sx={{ color: auditDashboardTheme.colors.text, fontSize: 14, fontWeight: 700 }}>
                  {issue.type}
                </Typography>
                {issue.game_packs.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mt: 0.75 }}>
                    {issue.game_packs.map(({ game_id, pack_id }) => (
                      <Box
                        component="span"
                        key={`${game_id}-${pack_id || 'game'}`}
                        sx={{ bgcolor: auditDashboardTheme.colors.blueSoft, borderRadius: '5px', color: auditDashboardTheme.colors.blue, fontSize: 12, fontWeight: 600, px: 0.75, py: 0.25 }}
                      >
                        {pack_id ? `${game_id}-${pack_id}` : `Game ${game_id}`}
                      </Box>
                    ))}
                  </Box>
                )}
                <Typography sx={{ color: auditDashboardTheme.colors.textMutedStrong, fontSize: 12.5, lineHeight: 1.45, mt: 0.75 }}>
                  {issue.impact}
                </Typography>
                <RootCauseAuditDetails audits={issue.root_cause_audits} />
              </Box>
            ))}

            {report.unexplained_games.map((game) => (
              <Box key={game.game_id} sx={{ bgcolor: auditDashboardTheme.colors.headerProgress, borderRadius: '8px', p: 1.25 }}>
                <Typography sx={{ color: auditDashboardTheme.colors.text, fontSize: 13.5, fontWeight: 700 }}>
                  Unexplained discrepancy · Game {game.game_id}
                </Typography>
                <Typography sx={{ color: auditDashboardTheme.colors.textMutedStrong, fontSize: 12.5, mt: 0.4 }}>
                  {`${Math.abs(game.variance.ticket_quantity)} tickets · ${formatCurrency(Math.abs(game.variance.sales_amount))}`}
                </Typography>
              </Box>
            ))}

            {report.outcome === 'matched' && report.identified_issues.length === 0 && report.unexplained_games.length === 0 && (
              <Typography sx={{ color: auditDashboardTheme.colors.textMutedStrong, fontSize: 13 }}>
                The investigator did not find an unexplained scratcher issue for this date.
              </Typography>
            )}

            {report.errors.map((message) => (
              <Typography key={message} sx={{ color: auditDashboardTheme.colors.textNegative, fontSize: 12.5 }}>
                {message}
              </Typography>
            ))}
          </Stack>
        )}
      </Stack>
    )}
  </CustomDialog>
);

export default StoreInvestigationDialog;
